import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal } from "../slices/authSlice";
const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_TARGET_API}`,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = Cookies.get("access_token");
      const refresh_token = Cookies.get("refresh_token");

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      const errorMessage = err.response?.data?.error || "";

      if (
        err.response?.status === 401 &&
        errorMessage.includes("access token has expired") &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        const accessToken = Cookies.get("access_token");
        const refreshToken = Cookies.get("refresh_token");
        if (!refreshToken) {
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(err);
        }

        try {
          // Determine the correct tenant ID
          const tenantId = getTenantId();

          const refreshHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshToken}`,
            "x-tenant-id": tenantId,
          };
          // Request new tokens from `/core/refresh`
          const { data } = await axios.post(
            `${process.env.REACT_APP_AUTH_API}/core/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            { headers: refreshHeaders },
          );

          // Save new tokens in Cookies
          Cookies.set("access_token", data?.access_token, {
            secure: true,
          });
          Cookies.set("refresh_token", data?.refresh_token, {
            secure: true,
          });

          // Retry the original request with the new token
          originalConfig.headers["Authorization"] =
            `Bearer ${data?.access_token}`;
          originalConfig.headers["Refresh"] = `Bearer ${data?.refresh_token}`;
          return axiosInstance(originalConfig);
        } catch (_error) {
          // Refresh failed, clear cookies and redirect to login
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(_error);
        }
      }

      return Promise.reject(err);
    },
  );
};

const toDDMMYYYY = (value) => {
  if (!value) return null;
  try {
    const s = String(value).trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s; // already DD/MM/YYYY
    const parts = s.split(/[-/]/);
    if (parts.length === 3) {
      // Handle common YYYY-MM-DD / YYYY/MM/DD
      if (parts[0].length === 4) {
        const [yyyy, mm, dd] = parts;
        return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
      }
      // Handle common DD-MM-YYYY
      if (parts[2].length === 4) {
        const [dd, mm, yyyy] = parts;
        return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
      }
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  } catch {}
  return String(value);
};

const axiosBaseQuery =
  () =>
  async (
    { url, method, body, params, ...requestOpts },
    { getState, dispatch },
  ) => {
    setUpInterceptors(getState, dispatch);

    const tenantId = getTenantId();

    const enhancedHeaders = {
      ...requestOpts.headers,
      "X-Tenant-Id": tenantId,
    };

    try {
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: enhancedHeaders,
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const targetsettingapi = createApi({
  reducerPath: "targetsettingapi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["targetsetting"],
  endpoints: (builder) => ({
    getToken: builder.mutation({
      query: () => ({
        url: `login`,
        method: "POST",
        body: {
          API_KEY: process.env.REACT_APP_ACTIVITIES_HOST_API_KEY,
        },
      }),
      invalidatesTags: ["activities"],
    }),
    Createtarget: builder.mutation({
      query: ({ payload }) => {
        const tenantId = getTenantId();

        return {
          url: `/create_target/`,
          method: "POST",
          body: payload,
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    GetTargets: builder.query({
      query: ({ token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_targets/`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    DeleteTarget: builder.mutation({
      query: ({ targetNames }) => {
        const tenantId = getTenantId();

        return {
          url: `/delete_target/`,
          method: "POST",
          body: { targetNames },
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    Getcategoriesdata: builder.query({
      query: ({ taxonomy, access_token, token, target }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_categories_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            taxonomy,
            target,
            access_token,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    Deletecategoriesdata: builder.mutation({
      query: ({ level, access_token, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/delete_category_data/`,
          method: "POST",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          body: {
            level,
            access_token,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    Getgraphcategoriesdata: builder.query({
      query: ({ taxonomy, node, target, access_token, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_graph_coordinates`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            taxonomy,
            node,
            target,
            access_token,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),

    Getscopecategoriesdata: builder.query({
      query: ({ scope, node, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_category_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            scope,
            node,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),

    Getscopecategoriesdatascope1: builder.query({
      query: ({ scope, node, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_category_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            scope,
            node,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    Getscopecategoriesdatascope2: builder.query({
      query: ({ scope, node, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_category_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            scope,
            node,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    Getscopecategoriesdatascope3: builder.query({
      query: ({ scope, node, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_category_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            scope,
            node,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),

    SetscopeTarget: builder.mutation({
      query: ({ payload }) => {
        const tenantId = getTenantId();

        return {
          url: `/set_category_data/`,
          method: "POST",
          body: payload,
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    CreateTargets: builder.mutation({
      query: (payload) => {
        const tenantId = getTenantId();
        return {
          url: `/target/create_targets`,
          method: "POST",
          body: payload,
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),

    GetTargetsList: builder.query({
      query: () => {
        return {
          url: `/target/get_targets`,
          method: "GET",
        };
      },
      providesTags: ["targetsetting"],
    }),
    DeleteTargetById: builder.mutation({
      query: (targetId) => {
        const tenantId = getTenantId();
        return {
          url: `/target/delete`,
          method: "DELETE",
          params: { target_id: targetId },
          headers: { "X-Tenant-Id": tenantId },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    // TargetSettingapi.js (or wherever your api slice is)
    setPercentage: builder.mutation({
      // 👇 accept ANY payload and forward it directly
      query: (payload) => {
        const tenantId = getTenantId();
        return {
          url: `/target/set_percentage`,
          method: "POST",
          body: payload, // ✅ pass-through (node_id, target_id, percentage_data)
          headers: {
            "x-tenant-id": tenantId, // keep your header if required
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),

    baseMetricCheck: builder.query({
      query: ({ nodeId, targetId, metricId }) => {
        console.log("Queryparams:", { nodeId, targetId, metricId }); // Log the params to see if they're correctly passed
        return {
          url: `/target/base-matric-check`,
          method: "GET",
          params: {
            node_id: nodeId,
            target_id: targetId,
            metric_id: metricId,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),

    getDashboardData: builder.query({
      query: ({ node_id, target_id }) => ({
        url: `/target/get_dashboard_data`, // Your API path
        method: "GET",
        params: { node_id, target_id }, // Pass node_id and target_id as params
      }),
      providesTags: ["targetsetting"],
    }),

    GetPercentageData: builder.query({
      query: ({ node_id, target_id, token }) => {
        const tenantId = getTenantId();
        return {
          url: `/target/get_percentage_data`,
          method: "GET",
          params: { node_id, target_id },
          headers: {
            "X-Tenant-Id": tenantId,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    downloadDashboardData: builder.query({
      query: ({ node_id, target_id }) => ({
        url: `/target/download_dashboard_data`,
        method: "GET",
        params: { node_id, target_id },
      }),
    }),
    templateList: builder.query({
      query: ({ search = "", page = 1, limit = 10, category_id = null }) => ({
        url: `/cs/templates`,
        params: {
          page,
          limit,
          ...(search?.trim() ? { search: search.trim() } : {}),
          ...(category_id ? { category_id } : {}),
        },
      }),
    }),
    templateFields: builder.query({
  query: (arg) => {
    const templateId =
      typeof arg === "object" ? arg.templateId || arg.template_id : arg;

    const currency =
      typeof arg === "object" ? arg.currency || "INR" : "INR";

    return {
      url: `/cs/templates/${templateId}/fields`,
      method: "GET",
      params: {
        currency,
      },
    };
  },
}),
    createInitiative: builder.mutation({
      query: (payload) => {
        return {
          url: `/cs/initiatives`,
          body: payload,
          method: "POST",
        };
      },
      invalidatesTags: ["initiative"],
    }),
    createInitiativeCustom: builder.mutation({
      query: ({ draft_id }) => {
        return {
          url: `/cs/initiatives`,
          body: { draft_id },
          method: "POST",
        };
      },
      invalidatesTags: ["initiative"],
    }),
    getInitiatives: builder.query({
      query: ({ page = 1, limit = 10, search = "" } = {}) => {
        return {
          url: `/cs/initiatives`,
          params: {
            page,
            limit,
            ...(search ? { search } : {}),
          },
        };
      },
      providesTags: ["initiative"],
    }),
    deleteInitiative: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/cs/initiatives/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["initiative"],
    }),
    initiativeDetails: builder.query({
      query: ({ id }) => {
        return {
          url: `/cs/initiatives/${id}`,
        };
      },
      providesTags: ["save"],
    }),
    updateInitiative: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/cs/initiatives/${id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["initiative"],
    }),
    calculateInitiative: builder.mutation({
      query: (body) => {
        return {
          url: `/cs/initiatives/calculate`,
          method: "POST",
          body,
        };
      },
    }),
    maccChartData: builder.query({
      query: () => {
        return {
          url: `/cs/macc`,
        };
      },
    }),
    categoryList: builder.query({
      query: () => {
        return {
          url: `/cs/template-categories`,
        };
      },
    }),
    compareCharts: builder.query({
      query: ({ initiativeIds }) => {
        return {
          url: `/cs/initiatives/compare`,
          params: {
            ids: initiativeIds.join(","),
          },
        };
      },
    }),

    draftInitiative: builder.mutation({
      query: (body) => {
        return {
          url: `/cs/initiatives/draft`,
          body,
          method: "POST",
        };
      },
      invalidatesTags: ["initiative", "save"],
    }),
    templateCategories: builder.query({
      query: () => {
        return {
          url: `/cs/template-categories`,
        };
      },
    }),

    currency: builder.query({
      query: () => {
        return {
          url: `/cs/currencies`
        }
      }
    })
  }),
});

export const {
  useCreatetargetMutation,
  useDeleteTargetMutation,
  useSetPercentageMutation,
  useSetscopeTargetMutation,
  useGetscopecategoriesdatascope1Query,
  useGetscopecategoriesdatascope2Query,
  useGetPercentageDataQuery,
  useGetscopecategoriesdatascope3Query,
  useGetTargetsQuery,
  useCreateTargetsMutation,
  useDeleteTargetByIdMutation,
  useGetgraphcategoriesdataQuery,
  useGetDashboardDataQuery,
  useGetTokenMutation,
  useGetTargetsListQuery,
  useGetscopecategoriesdataQuery,
  useDownloadDashboardDataQuery,
  useLazyDownloadDashboardDataQuery,
  useDeletecategoriesdataMutation,
  useGetcategoriesdataQuery,
  useBaseMetricCheckQuery,
  useLazyTemplateFieldsQuery,
  useTemplateListQuery,
  useCreateInitiativeMutation,
  useCreateInitiativeCustomMutation,
  useGetInitiativesQuery,
  useDeleteInitiativeMutation,
  useInitiativeDetailsQuery,
  useUpdateInitiativeMutation,
  useCalculateInitiativeMutation,
  useMaccChartDataQuery,
  useCategoryListQuery,
  useCompareChartsQuery,
  useDraftInitiativeMutation,
  useTemplateCategoriesQuery,
  useCurrencyQuery
} = targetsettingapi;
